import { lazy, Suspense } from "preact/compat";
import { useEffect, useRef, useState } from "preact/hooks";

import { AddHealthNoteSheet } from "./AddHealthNoteSheet";
import { AddWeightSheet } from "./AddWeightSheet";
import type { AssigneeOption } from "./AssigneePickerSheet";
import { AssigneePickerSheet } from "./AssigneePickerSheet";
import { CalendarCreateSheet } from "./CalendarCreateSheet";
import { Snackbar } from "./Snackbar";
import { useAdminCadences } from "../hooks/useAdminCadences";
import { useAnimals } from "../hooks/useAnimals";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useModalA11y } from "../hooks/useModalA11y";
import { useAnyActionSnackbarVisible } from "../hooks/useSnackbarPresence";
import { createAssignment, logAnimalWeight, logHealthEvent } from "../lib/api";
import { addedSnackbarMessage } from "../lib/calendarCopy";
import { toISODate, todayLocal } from "../lib/calendarDates";
import { SAVED_MESSAGE } from "../lib/recordsCopy";
import {
  QUICK_ADD_ANIMAL_PICKER_TITLE,
  QUICK_ADD_BUTTON_LABEL,
  QUICK_ADD_FAB_LABEL,
  QUICK_ADD_MENU_HEADING,
  QUICK_ADD_ROW_LABEL_BY_ACTION,
  QUICK_ADD_TASK_SHEET_TITLE,
  type QuickAddAction,
} from "../lib/quickAddCopy";

// Sprint-004 B4/B5's own precedent (`app.tsx`'s `AdminView` code-split) —
// `ChoreEditorOverlay` (and its `useAdminCadences` chrome) is admin-only
// weight that a manager session (which also mounts this component, since
// Quick Add is `canHandOutWork`-gated, not `isAdmin`-gated) should never
// have to download just because Quick Add happens to be visible to them
// too. Lazy-loaded exactly like `AdminView` is.
const ChoreEditorOverlay = lazy(() =>
  import("../views/ChoreEditorOverlay").then((m) => ({ default: m.ChoreEditorOverlay })),
);

export interface QuickAddProps {
  /** Absent entirely (not present-and-inert) when `false` — every one of
   * Quick Add's four actions requires at least this gate server-side
   * (§0/§11.2, UX-validated, no change). */
  canHandOutWork: boolean;
  isAdmin: boolean;
  /** The same already-fetched team roster `CalendarScreen`'s "Who does
   * it" picker uses — no second fetch. */
  roster: AssigneeOption[];
}

/**
 * Sprint-010 C4 (design-phase-c.md §5) — global Quick Add: one persistent
 * button (desktop, fixed top-right) or FAB (phone, fixed bottom-right,
 * above `TabBar`), mounted once by `app.tsx`. Composes over EXISTING
 * endpoints only (Decision 9 — no new backend) — every mutation below
 * reuses the exact create/edit call its own non-Quick-Add caller already
 * has, including the required client-generated `idempotency_key`
 * convention.
 *
 * Menu vs. direct shortcut (§5.3): admin sees all four actions behind a
 * menu; a manager who is not admin can only ever "Add a task," so the
 * menu is skipped entirely — tapping Quick Add opens that one sheet
 * directly (a one-item menu offers no real choice).
 */
export function QuickAdd({ canHandOutWork, isAdmin, roster }: QuickAddProps) {
  const isDesktop = useIsDesktop();
  // QA-B1 fix (design-phase-c.md §5.2 note) — an app-wide signal, not
  // this component's own local `snackbarMessage`: an action Snackbar
  // raised by a completely different screen (Today's tap-to-done,
  // Calendar's reschedule/skip Undo) collides with this SAME mounted-
  // once FAB just as much as one of Quick Add's own toasts would.
  const anyActionSnackbarVisible = useAnyActionSnackbarVisible();
  const [menuOpen, setMenuOpen] = useState(false);
  const [action, setAction] = useState<QuickAddAction | null>(null);
  const [pickingAnimalFor, setPickingAnimalFor] = useState<"health-note" | "weight" | null>(null);
  const [pickedAnimalId, setPickedAnimalId] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  // §5.4 — the animal roster is fetched LAZILY, only once "Log a health
  // note"/"Log a weight" is actually picked, never eagerly on app load.
  const animals = useAnimals(
    pickingAnimalFor !== null || action === "health-note" || action === "weight",
  );
  // Cadences are needed only for "Add a chore" — same lazy-`enabled` gate.
  const cadences = useAdminCadences(action === "chore");

  useEffect(() => {
    if (!snackbarMessage) return;
    const timer = setTimeout(() => setSnackbarMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [snackbarMessage]);

  if (!canHandOutWork) return null;

  const availableActions: QuickAddAction[] = isAdmin
    ? ["task", "health-note", "weight", "chore"]
    : ["task"];

  function closeAll() {
    setMenuOpen(false);
    setAction(null);
    setPickingAnimalFor(null);
    setPickedAnimalId(null);
  }

  function startAction(a: QuickAddAction) {
    setMenuOpen(false);
    if (a === "health-note" || a === "weight") {
      setPickingAnimalFor(a);
    } else {
      setAction(a);
    }
  }

  function openQuickAdd() {
    if (availableActions.length === 1) {
      startAction(availableActions[0]!);
    } else {
      setMenuOpen(true);
    }
  }

  const pickedAnimal = animals.animals?.find((a) => a.id === pickedAnimalId) ?? null;

  async function handleAddTask(input: { title: string; assigneeId?: string }) {
    try {
      await createAssignment({
        idempotency_key: crypto.randomUUID(),
        title: input.title,
        assignee_id: input.assigneeId,
        due_date: toISODate(todayLocal()),
      });
      setSnackbarMessage(addedSnackbarMessage(input.title, "today"));
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }

  return (
    <>
      {isDesktop ? (
        <button type="button" class="quick-add-btn" onClick={openQuickAdd}>
          {QUICK_ADD_BUTTON_LABEL}
        </button>
      ) : (
        <button
          type="button"
          // QA-B1 fix (design-phase-c.md §5.2 note) — raised clear of an
          // action Snackbar's Undo button rather than sharing its tap
          // region; see `styles.css`'s `.quick-add-fab.is-raised`.
          class={anyActionSnackbarVisible ? "quick-add-fab is-raised" : "quick-add-fab"}
          aria-label={QUICK_ADD_FAB_LABEL}
          onClick={openQuickAdd}
        >
          +
        </button>
      )}

      {availableActions.length > 1 ? (
        <QuickAddMenuSheet
          open={menuOpen}
          actions={availableActions}
          onSelect={startAction}
          onCancel={() => setMenuOpen(false)}
        />
      ) : null}

      <AssigneePickerSheet
        open={pickingAnimalFor !== null}
        pieceTitle=""
        title={QUICK_ADD_ANIMAL_PICKER_TITLE}
        showAvatar={false}
        options={(animals.animals ?? []).map((a) => ({ id: a.id, name: a.name }))}
        selectedId={null}
        onSelect={(id) => {
          const forAction = pickingAnimalFor;
          setPickedAnimalId(id);
          setPickingAnimalFor(null);
          if (forAction) setAction(forAction);
        }}
        onCancel={closeAll}
      />

      <CalendarCreateSheet
        open={action === "task"}
        dateLabel="today"
        titleOverride={QUICK_ADD_TASK_SHEET_TITLE}
        roster={roster}
        onAdd={handleAddTask}
        onCancel={closeAll}
      />

      <AddHealthNoteSheet
        open={action === "health-note" && pickedAnimal !== null}
        animalName={pickedAnimal?.name ?? ""}
        onAdd={async (note, eventType) => {
          if (!pickedAnimal) return;
          await logHealthEvent(pickedAnimal.id, {
            idempotency_key: crypto.randomUUID(),
            note,
            event_type: eventType,
          });
          setSnackbarMessage(`Saved a note for ${pickedAnimal.name}.`);
          closeAll();
        }}
        onCancel={closeAll}
      />

      <AddWeightSheet
        open={action === "weight" && pickedAnimal !== null}
        animalName={pickedAnimal?.name ?? ""}
        onAdd={async (weightLb, weighedOn, note) => {
          if (!pickedAnimal) return;
          await logAnimalWeight(pickedAnimal.id, {
            idempotency_key: crypto.randomUUID(),
            weight_lb: weightLb,
            weighed_on: weighedOn,
            note,
          });
          setSnackbarMessage(SAVED_MESSAGE);
          closeAll();
        }}
        onCancel={closeAll}
      />

      {action === "chore" ? (
        <Suspense fallback={null}>
          <ChoreEditorOverlay
            cadence={null}
            roster={roster}
            cadences={cadences}
            onClose={closeAll}
            onNotify={(message) => setSnackbarMessage(message)}
          />
        </Suspense>
      ) : null}

      <Snackbar
        state={snackbarMessage ? { message: snackbarMessage } : null}
        onDismiss={() => setSnackbarMessage(null)}
      />
    </>
  );
}

function QuickAddMenuSheet({
  open,
  actions,
  onSelect,
  onCancel,
}: {
  open: boolean;
  actions: QuickAddAction[];
  onSelect: (action: QuickAddAction) => void;
  onCancel: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLButtonElement>(null);
  // Sprint-011 C1 (TD-012) — backdrop + Cancel route through the hook's
  // returned `close`, not `onCancel` directly.
  const { close } = useModalA11y(containerRef, {
    active: open,
    onClose: onCancel,
    initialFocusRef: firstRef,
  });

  if (!open) return null;

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quick-add-menu-title"
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id="quick-add-menu-title">
          {QUICK_ADD_MENU_HEADING}
        </h2>
        <ul class="admin-list">
          {actions.map((a, index) => (
            <li key={a}>
              <button
                ref={index === 0 ? firstRef : undefined}
                type="button"
                class="admin-row"
                onClick={() => onSelect(a)}
              >
                <span class="admin-row-main">
                  <span class="admin-row-name">{QUICK_ADD_ROW_LABEL_BY_ACTION[a]}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
        <button type="button" class="secondary-btn" style={{ marginTop: "10px" }} onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
